import { AnnotationIcon, EyeIcon, UploadIcon } from '@heroicons/react/outline';
import { pdf } from '@react-pdf/renderer';
import Button from 'components/atoms/Button';
import Heading from 'components/atoms/Heading';
import Text from 'components/atoms/Text';
import React, { useEffect, useState } from 'react';
import {
	useAssignIncidentMutation,
	useCreateIncidentActivityMutation,
	useGetIncidentMutation,
	useGetIncidentForCompletionCheckMutation,
} from 'redux/api/vehicleIncidents';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import {
	openCommentModal,
	openIncidentModal,
} from 'redux/slices/vehicleIncidentsSlice';
import { IVehicleIncident, IncidentPdfTypes } from 'types/VehicleIncidents';
import { hasAccess } from 'utils/permissions';
import { toast } from 'react-toastify';
import { useGetListQuery as useGetLogoListQuery } from 'redux/api/pdfCompanyLogoAPI';

import StructuredPdfDocument from '../StructuredPdfDocument';
import ToggleButtonV2 from '../../atoms/ToggleButtonV2';
import RadioV2 from '../../atoms/RadioV2';
import { PlusIcon } from '@heroicons/react/solid';
import IncidentPdfDownloadDialog from '../IncidentPdfDownloadDialog';
import { getIncompleteIncidentSections } from 'utils/getIncompleteIncidentSections';

export interface IIncidentCard {
	incident: IVehicleIncident;
	refetchData: () => void;
	removeIdFromList: (
		id: string | number,
		isNuisanceOrDuplicate: boolean
	) => void;
}

const IncidentCard: React.FC<IIncidentCard> = ({
	incident,
	refetchData,
	removeIdFromList,
}) => {
	const dispatch = useAppDispatch();

	const [assignIncident] = useAssignIncidentMutation();
	const [getIncidentForCompletionCheck] =
		useGetIncidentForCompletionCheckMutation();
	const [isDuplicateOrNuisance, setIsDuplicateOrNuisance] = useState(
		['duplicate', 'nuisance'].includes(incident.special_status || '') &&
			incident.status !== 'open'
	);
	const [specialStatus, setSpecialStatus] = useState<
		'duplicate' | 'nuisance' | null
	>(incident.special_status || null);
	const [status, setStatus] = useState(incident.status || 'open');

	const [createIncidentActivity] = useCreateIncidentActivityMutation();
	const userId = useAppSelector((state) => state.user.id);

	const userPermission = useAppSelector((state) => state.user.permissions);
	const hasWriteAccess = () => {
		return hasAccess(
			userPermission,
			'admin-manager',
			'incident_level',
			'write',
			incident?.branch.id
		);
	};
	const hasStatusWriteAccess = () => {
		return hasAccess(
			userPermission,
			'admin-manager',
			'incident_level',
			'write',
			incident?.branch.id
		);
	};
	const hasViewAccessWhenClosed = () => {
		return hasAccess(
			userPermission,
			'admin-manager',
			'incident_level',
			'read',
			incident?.branch.id
		);
	};

	const getUserRoleForIncident = ():
		| 'staff'
		| 'manager'
		| 'admin-manager'
		| null => {
		const permission = userPermission?.find(
			(perm) => perm.branch?.id?.toString() === incident?.branch.id?.toString()
		);
		return permission?.role || null;
	};

	const submitButtonDisabled = () => {
		if (hasStatusWriteAccess()) {
			return (
				incident.status === status && incident.special_status === specialStatus
			);
		} else {
			return (
				(incident.special_status === null && !isDuplicateOrNuisance) ||
				(incident.special_status === specialStatus && isDuplicateOrNuisance)
			);
		}
	};

	const statusChangeVisible = () => {
		return hasStatusWriteAccess();
	};

	const setIsDuplicateOrNuisanceHandler = (dupeOrNuisance: boolean) => {
		setIsDuplicateOrNuisance(dupeOrNuisance);

		if (!dupeOrNuisance) {
			setSpecialStatus(null);
		} else {
			setSpecialStatus('duplicate');
			setStatus('closed');
		}
	};

	const handleOpenComments = () => {
		const payload = {
			commentIncidentId: incident.id,
			incidentBranchId: incident?.branch?.id,
		};
		dispatch(openCommentModal(payload));
	};

	const handleOpenIncidentReport = () => {
		try {
			const payload = {
				incidentReportId: incident.id,
				incidentBranchId: incident.branch.id,
				incidentSection1Id: incident?.incident_section_1?.id,
				incidentSection2Id: incident?.incident_section_2?.id,
				incidentSection3Id: incident?.incident_section_3?.id,
				incidentSection4Id: incident?.incident_section_4?.id,
				incidentSection5Id: incident?.incident_section_5?.id,
				incidentSection6Id: incident?.incident_section_6?.id,
				incidentSection7Id: incident?.incident_section_7?.id,
				iCamIsVisible:
					incident?.incident_section_5?.incident_investigation_required,
				status: incident?.status,
			};
			dispatch(openIncidentModal(payload));
			createIncidentActivity({
				incident: incident.id,
				user: userId,
				type: 'view report',
				content: 'Report opened',
			});
		} catch (error) {
			if (error instanceof Error) {
				throw new Error(error.message);
			}
		}
	};

	/*
	const isUserAssigned = () => {
		return incident?.incident_section_7?.actions?.reduce(
			(accumulator, action) => {
				if (action.assignee !== null) {
					return true;
				} else {
					return accumulator;
				}
			},
			false
		);
	};
	*/

	// we want to skip this check for now at the clients request. Keeping the code above for future reference/usage
	const isUserAssigned = () => {
		return false;
	};

	const handleUpdateIncident = async () => {
		if (isUserAssigned() && status === 'closed') {
			toast.error('Sorry, you cannot close an incident with assigned users!');
			return;
		}

		const nextSpecialStatus = isDuplicateOrNuisance ? specialStatus : null;
		if (status === 'closed' && !nextSpecialStatus) {
			try {
				const { incidentData } = await getIncidentForCompletionCheck(
					incident.id
				).unwrap();
				const incompleteSections = getIncompleteIncidentSections(incidentData);
				if (!incidentData.completed || incompleteSections.length > 0) {
					const unfinished = incompleteSections.length
						? incompleteSections.join(', ')
						: 'Report completion';
					toast.warning(
						`This incident cannot be closed yet. Finish these sections first: ${unfinished}.`
					);
					setStatus('open');
					return;
				}
			} catch {
				toast.error('Unable to verify report completion. Please try again.');
				return;
			}
		}

		if (status === 'open') {
			setSpecialStatus(null);
			setIsDuplicateOrNuisance(false);
		}

		try {
			await assignIncident({
				id: incident.id,
				body: {
					status: status,
					special_status: nextSpecialStatus,
				},
			}).unwrap();
		} catch (error) {
			const responseError = error as {
				status?: number;
				data?: { message?: string };
			};
			if (responseError.status === 409) {
				setStatus('open');
			}
			toast.error(
				responseError.data?.message || 'Unable to update incident status.'
			);
			return;
		}

		if (isDuplicateOrNuisance || status === 'closed') {
			removeIdFromList(incident.id, isDuplicateOrNuisance);
		}

		refetchData();
	};

	const [
		getIncident,
		{ data: incidentDetail, isSuccess: isSuccessGetIncidentDetail },
	] = useGetIncidentMutation();
	const [exportDialogOpen, setExportDialogOpen] = useState(false);
	const [exportType, setExportType] = useState<IncidentPdfTypes>('full');

	const handleExportIncidentReport = (type: IncidentPdfTypes) => {
		setExportType(type);
		getIncident(incident.id);
	};

	const { data: companyLogos, isSuccess: isLogoSuccess } =
		useGetLogoListQuery();

	useEffect(() => {
		if (isSuccessGetIncidentDetail && incidentDetail && isLogoSuccess) {
			pdf(
				StructuredPdfDocument({
					incident: incidentDetail.incidentData,
					logos: companyLogos || [],
					exportType: exportType,
				})
			)
				.toBlob()
				.then((blob) => {
					const url = window.URL.createObjectURL(blob);
					const tempLink = document.createElement('a');
					tempLink.href = url;
					tempLink.setAttribute('download', `Incident-${incident.id}.pdf`);
					tempLink.click();

					createIncidentActivity({
						incident: incident.id,
						user: userId,
						type: 'export',
						content: 'Export report to PDF',
					});
				});
		}
		// eslint-disable-next-line
	}, [incidentDetail, isSuccessGetIncidentDetail, exportType]);

	const isUserAssignedToIncident = () => {
		return incident?.assignees?.some(
			(assignee) => assignee.id?.toString() === userId?.toString()
		);
	};

	const openIncidentReportDisabled = () => {
		return (
			incident.status === 'closed' &&
			!hasViewAccessWhenClosed() &&
			!isUserAssignedToIncident()
		);
	};

	return (
		<div className="w-full px-10 pb-10 pt-8 border-t border-primary">
			<div className="flex">
				<div className="flex-col w-1/2 xl:w-3/4">
					<Heading type="h2" className="mb-6">
						Overview
					</Heading>
					<div className="flex pr-1 lg:pr-12 2xl:pr-32 justify-start 2xl:justify-between space-x-1 lg:space-x-2 2xl:space-x-32">
						<div className="min-w-[200px]">
							<Heading type="h3" className="mb-6">
								Description
							</Heading>
							<Text className="max-w-lg mb-10 text-sm">
								{incident.description}
							</Text>
							<div className="flex w-[350px]">
								<Text
									className="flex basis-1/2 text-sm mb-2 w-[200px]"
									type="bold"
								>
									Incident Category
								</Text>
								{incident?.incident_section_4?.incident_category.length > 0 ? (
									<div>
										{incident.incident_section_4.incident_category.map(
											(category) => (
												<Text
													key={category.id}
													className="flex basis-1/2 text-sm mb-2"
												>
													{category.label}
												</Text>
											)
										)}
									</div>
								) : (
									<Text className="flex basis-1/2 text-sm mb-2">
										No Category
									</Text>
								)}
							</div>
							<div className="h-0 border-t-[1px] border-primary-200 w-full my-5" />
							<div className="flex w-[350px]">
								<Text
									className="flex basis-1/2 mb-6 text-sm w-[200px]"
									type="bold"
								>
									Client
								</Text>
								<Text className="flex basis-1/2 mb-6 text-sm">
									{incident.client}
								</Text>
							</div>
							<div className="h-0 border-t-[1px] border-primary-200 w-full my-5" />
						</div>
						<div className="hidden xl:flex xl:flex-col">
							<div className="mb-4 space-y-2">
								<Text type="bold">Created:</Text>
								<Text className="text-sm">
									{new Date(incident.submitted_at).toLocaleDateString('en-AU')}
								</Text>
							</div>
							<div className="mb-4 space-y-2">
								<Text type="bold">Last Action:</Text>
								{incident?.incident_activities ? (
									<>
										<Text className="text-sm">
											Name: {incident.incident_activities.name}
										</Text>
										<Text className="text-sm">
											Type: {incident.incident_activities.type}
										</Text>
										<Text className="text-sm">
											Date:{' '}
											{new Date(
												incident.incident_activities.createdAt
											).toLocaleDateString('en-AU')}
										</Text>
									</>
								) : (
									<Text className="text-sm">No Actions</Text>
								)}
							</div>
						</div>
						<div className="hidden xl:flex xl:flex-col">
							<div className="mb-4 space-y-2">
								<Text type="bold">Assigned to:</Text>
								{incident?.assignees?.map((assignee) => (
									<Text key={assignee.id} className="text-sm">
										{assignee.first_name} {assignee.last_name}
									</Text>
								))}
							</div>
							<div className="mb-4 space-y-2">
								<Text type="bold">Person Involved:</Text>
								{incident?.incident_section_2?.personInvolved?.map((person) => (
									<Text className="text-sm">{person.personInvolved_name}</Text>
								))}
							</div>
						</div>
					</div>
				</div>
				<div className="flex flex-1 w-1/2 xl:w-1/4 justify-end">
					<div className="max-w-sm min-w-[400px] w-full">
						<div className="flex flex-row justify-between items-start gap-[4px]">
							<div className="w-1/2">
								<Button
									className="min-w-[220px] font-bold text-[14px] tracking-[1.92px] !rounded-3xl px-[16px] py-[16px] "
									onClick={handleOpenIncidentReport}
									size="lg"
									isDisabled={openIncidentReportDisabled()}
									rightClickLink={`/incidents?view=${incident.id}`}
								>
									{incident.completed ? (
										<EyeIcon className="h-5 w-5 mr-1" />
									) : (
										<span className="mr-[4px]">
											<PlusIcon width="16px" height="16px" />
										</span>
									)}
									{incident.completed || incident.status === 'closed'
										? 'View Report'
										: 'Create Report'}
								</Button>
							</div>
							<Button
								type="quaternary"
								className="font-bold text-[14px] w-[170px] !rounded-3xl px-[40px] py-[16px] tracking-[1.92px]"
								onClick={handleOpenComments}
								size="lg"
							>
								<AnnotationIcon className="h-[20px] w-[20px] mr-1" />
								History
							</Button>
						</div>
						{['closed', 'open'].includes(incident.status) && (
							<>
								<Button
									className="font-bold text-[14px] w-full xl:mb-0 !rounded-3xl px-[40px] py-3 tracking-[1.92px] mt-[20px]"
									onClick={() => setExportDialogOpen(true)}
									size="md"
								>
									<UploadIcon className="h-5 w-5 mr-1" />
									Export to PDF
								</Button>
								<IncidentPdfDownloadDialog
									isOpen={exportDialogOpen}
									setIsOpen={setExportDialogOpen}
									onChange={(type) => {
										handleExportIncidentReport(type);
										setExportDialogOpen(false);
									}}
									userRole={getUserRoleForIncident()}
								/>
							</>
						)}
						{hasWriteAccess() && (
							<div>
								<div className="h-0 border-t-[1px] border-primary-200 w-full my-4" />
								<div className="flex flex-row items-center">
									<Text type="bold" className="mr-2">
										Is this a Nuisance or Duplicate Report?
									</Text>
									<ToggleButtonV2
										toggle={isDuplicateOrNuisance}
										onToggle={setIsDuplicateOrNuisanceHandler}
									/>
								</div>
								{isDuplicateOrNuisance && (
									<RadioV2
										className="mb-7"
										classNameWrapper="!gap-[16px]"
										options={[
											{ title: 'Duplicate', id: 'duplicate' },
											{ title: 'Nuisance Report', id: 'nuisance' },
										]}
										radioDirection="!flex !flex-row"
										defaultId={specialStatus || 'duplicate'}
										onChange={setSpecialStatus}
									/>
								)}
								<div className="h-0 border-t-[1px] border-primary-200 w-full my-4" />
								{statusChangeVisible() && (
									<>
										<div className="flex flex-row items-center">
											<Text type="bold" className="mr-2 my-2">
												Incident Status
											</Text>
										</div>
										<RadioV2
											className="mb-7"
											classNameWrapper="!gap-[16px]"
											options={[
												{ title: 'Open', id: 'open' },
												{ title: 'Closed', id: 'closed' },
											]}
											radioDirection="!flex !flex-row"
											defaultId={status || 'open'}
											onChange={setStatus}
										/>
										<div className="h-0 border-t-[1px] border-primary-200 w-full my-4" />
									</>
								)}
								<div className="flex justify-end">
									<Button
										navigate="next"
										onClick={handleUpdateIncident}
										className="font-bold text-[14px] !rounded-3xl px-[40px] py-[16px] tracking-[1.92px]"
										isDisabled={submitButtonDisabled()}
									>
										Submit
									</Button>
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
			{/*tidy up component for responsive desgin once data coming from API */}
			<div className="flex justify-between mt-8 w-full xl:hidden">
				<div className="mb-4 space-y-2">
					<Text type="bold">Created:</Text>
					<Text className="text-sm">
						{new Date(incident.submitted_at).toLocaleDateString('en-AU')}
					</Text>
				</div>
				<div className="mb-4 space-y-2">
					<Text type="bold">Last Activity:</Text>
					{incident?.incident_activities ? (
						<>
							<Text className="text-sm">
								Name: {incident.incident_activities.name}
							</Text>
							<Text className="text-sm">
								Type: {incident.incident_activities.type}
							</Text>
							<Text className="text-sm">
								Date:{' '}
								{new Date(
									incident.incident_activities.createdAt
								).toLocaleDateString('en-AU')}
							</Text>
						</>
					) : (
						<Text className="text-sm">No Actions</Text>
					)}
				</div>
				<div className="mb-4 space-y-2">
					<Text type="bold">Assigned to:</Text>
					{incident?.assignees?.map((assignee) => (
						<Text key={assignee.id} className="text-sm">
							{assignee.first_name} {assignee.last_name}
						</Text>
					))}
				</div>
			</div>
		</div>
	);
};

export default IncidentCard;
